import { uploadFileToS3 } from './s3Utils';
import { buildCloudFrontUrl, getExtension } from './pathUtils';
import { createCanvas, canvasToBlob } from './canvasUtils';

type VectorType = 'base' | 'vector' | 'vector-svg' | 'vector-pdf';

const THUMBNAIL_SIZE = 50;
// Fine <pattern> tiles wash out unless rasterized well above thumbnail size; 1024 matched Inkscape on prod SVGs
const THUMBNAIL_RENDER_SIZE = 1024;

export const getVectorTypeByFileExtension = (extension: string): VectorType => {
    const uploadTypes: Record<string, VectorType> = {
        eps: 'vector',
        svg: 'vector-svg',
        pdf: 'vector-pdf'
    };
    return uploadTypes[extension];
};

export const uploadVectorToS3 = async (
    vector: File,
    includeDimensions: boolean,
    generateThumb: boolean,
    vectorType?: VectorType,
    progressCallback?: (type: 'upload' | 'process', progress: number) => void
) => {
    const type = vectorType || getVectorTypeByFileExtension(getExtension(vector.name));
    // SVG thumbnails are rasterized here; core only falls back to Inkscape when the browser can't render it
    const clientThumbnail = generateThumb && type === 'vector-svg' ? await renderSvgThumbnail(vector) : undefined;
    // Get signed URL
    const response = await getVectorUploadS3SignedUrl(type, vector.name || '');

    // Upload to S3
    await uploadFileToS3(response.SignedUrl, response.Mime, vector, undefined, (progress) =>
        progressCallback?.('upload', progress)
    );

    // Uploaded before Validate so a failure falls back to Inkscape thumbnail without validating twice
    let thumbnailPath: string | undefined;
    if (clientThumbnail) {
        try {
            thumbnailPath = await uploadVectorThumbnail(clientThumbnail);
        } catch (error) {
            console.warn(`Thumbnail upload for '${vector.name}' failed, will use fallback`, error);
        }
    }

    // Validate the vector file
    progressCallback?.('process', 0);
    const validationResponse = await validateVectorFile(
        type,
        response.Name,
        includeDimensions,
        generateThumb && !thumbnailPath
    );
    if (thumbnailPath) validationResponse.ThumbnailPath = thumbnailPath;
    progressCallback?.('process', 100);

    return validationResponse;
};

// Uses the browser's SVG engine, not fabric: fabric drops <pattern>/<filter> and ignores the SVG's page size
const renderSvgThumbnail = async (svg: File): Promise<Blob | undefined> => {
    try {
        const doc = new DOMParser().parseFromString(await svg.text(), 'image/svg+xml');
        const root = doc.documentElement;
        let image = await loadSvgImage(doc);
        const { naturalWidth: width, naturalHeight: height } = image;
        if (!width || !height) throw new Error('SVG has no intrinsic size');
        // Without a viewBox the drawing doesn't scale with the canvas, it would end up tiny in a corner
        const sizeless = !['viewBox', 'width', 'height'].some((attribute) => root.hasAttribute(attribute));
        if (!root.hasAttribute('viewBox')) {
            root.setAttribute('viewBox', `0 0 ${width} ${height}`);
            root.setAttribute('width', `${width}`);
            root.setAttribute('height', `${height}`);
            image = await loadSvgImage(doc);
        }
        const renderScale = THUMBNAIL_RENDER_SIZE / Math.max(width, height);
        let canvas = drawScaled(image, width * renderScale, height * renderScale);
        // A sizeless SVG gets the browser's arbitrary 300x150 page, so crop to what is actually drawn
        if (sizeless) canvas = trimTransparent(canvas);
        const thumbnailScale = Math.min(THUMBNAIL_SIZE / canvas.width, THUMBNAIL_SIZE / canvas.height);
        const targetWidth = canvas.width * thumbnailScale;
        const targetHeight = canvas.height * thumbnailScale;
        // Halving steps average every source pixel; one big drawImage downscale skips most of them
        while (canvas.width / 2 > targetWidth) {
            canvas = drawScaled(canvas, canvas.width / 2, canvas.height / 2);
        }
        const thumbnail = drawScaled(canvas, targetWidth, targetHeight);
        return (await canvasToBlob(thumbnail, 'image/png')) ?? undefined;
    } catch (error) {
        console.warn(`'${svg.name}' could not be rendered client-side, core will generate its thumbnail`, error);
        return undefined;
    }
};

const loadSvgImage = async (doc: Document): Promise<HTMLImageElement> => {
    const svgText = new XMLSerializer().serializeToString(doc);
    const url = URL.createObjectURL(new Blob([svgText], { type: 'image/svg+xml' }));
    try {
        return await new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.onerror = () => reject(new Error('Browser failed to render the SVG'));
            image.src = url;
        });
    } finally {
        URL.revokeObjectURL(url);
    }
};

const trimTransparent = (canvas: OffscreenCanvas | HTMLCanvasElement) => {
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
    const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let [left, top, right, bottom] = [width, height, -1, -1];
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            if (data[(y * width + x) * 4 + 3] === 0) continue;
            left = Math.min(left, x);
            right = Math.max(right, x);
            top = Math.min(top, y);
            bottom = Math.max(bottom, y);
        }
    }
    if (right < 0) return canvas;
    const trimmed = createCanvas(right - left + 1, bottom - top + 1);
    const trimmedCtx = trimmed.getContext('2d') as CanvasRenderingContext2D;
    trimmedCtx.drawImage(canvas, left, top, trimmed.width, trimmed.height, 0, 0, trimmed.width, trimmed.height);
    return trimmed;
};

const drawScaled = (source: CanvasImageSource, width: number, height: number) => {
    const canvas = createCanvas(Math.max(1, Math.round(width)), Math.max(1, Math.round(height)));
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
    return canvas;
};

const uploadVectorThumbnail = async (thumbnail: Blob): Promise<string> => {
    const signedUrl = await getVectorThumbnailS3SignedUrl();
    await uploadFileToS3(signedUrl.SignedUrl, signedUrl.Mime, thumbnail, signedUrl.CacheControl);
    return signedUrl.Path;
};

export const getSvgStringFromPath = async (svgPath: string) => {
    const fetchHeaders = new Headers();
    fetchHeaders.append('pragma', 'no-cache');
    fetchHeaders.append('cache-control', 'no-cache');
    const fetchConfig: RequestInit = {
        method: 'GET',
        mode: 'cors',
        headers: fetchHeaders
    };

    // Try fetching locally
    const response = await fetch(svgPath, fetchConfig);

    if (response.ok) return response.text();

    if (response.status !== 404) {
        console.error(`Unexpected error from ${svgPath}: ${response.status}`);
        throw new Error('Unexpected error');
    }

    // Fallback to CloudFront URL
    const cloudFrontUrl = buildCloudFrontUrl(svgPath);
    const fallbackResponse = await fetch(cloudFrontUrl, fetchConfig);
    if (fallbackResponse.ok) return fallbackResponse.text();

    console.error(`CloudFront fallback failed: ${fallbackResponse.status}`);
    throw new Error('Could not fetch SVG from any source.');
};

import api from '@/api/baseApi';
import type { SignedUrlResponse } from '@/api/typings/image';

export interface ValidatedVector {
    EPS: string;
    SVG: string;
    Width: number;
    Height: number;
    ThumbnailPath: string;
    id: string;
}

export const getVectorUploadS3SignedUrl = async (type: string, filename: string): Promise<SignedUrlResponse> => {
    const { data } = await api.get<SignedUrlResponse>('/Vector/VectorUploadSignedUrl', {
        params: { type, filename }
    });
    return data;
};

export const getVectorThumbnailS3SignedUrl = async (): Promise<SignedUrlResponse> => {
    const { data } = await api.get<SignedUrlResponse>('/Vector/Thumbnail/SignedUrl');
    return data;
};

export const validateVectorFile = async (
    type: string,
    filename: string,
    includeDimensions: boolean,
    generateThumb: boolean
): Promise<ValidatedVector> => {
    const { data } = await api.get<ValidatedVector>(`/Vector/Validate/${encodeURIComponent(filename)}`, {
        params: {
            type,
            filename,
            includeDimensions,
            generateThumb
        }
    });
    return data;
};
